/**
 * A110Z — engraving colours (« encres ») of the design handoff: text tint, the thin shadow drawn
 * under the text, and the disc of the picker. Blanc is the default.
 */
export type InkId = 'or' | 'argent' | 'blanc' | 'noir' | 'rouge';

export interface Ink {
  id: InkId;
  /** Text colour. */
  color: string;
  /** Shadow colour, drawn 0.06 em below the text. */
  shadow: string;
  /** CSS background of the picker disc. */
  disc: string;
}

export const INKS: readonly Ink[] = [
  { id: 'or', color: '#d6b25e', shadow: 'rgba(40,20,0,.45)', disc: 'linear-gradient(135deg,#f3dc95,#b8892f 55%,#e9cf7e)' },
  { id: 'argent', color: '#d5d9dc', shadow: 'rgba(0,0,0,.4)', disc: 'linear-gradient(135deg,#f4f6f7,#9aa1a6 55%,#dfe3e5)' },
  { id: 'blanc', color: '#f8f5ef', shadow: 'rgba(0,0,0,.4)', disc: '#fbfaf7' },
  { id: 'noir', color: '#171615', shadow: 'rgba(255,255,255,.35)', disc: '#151515' },
  { id: 'rouge', color: '#c4372c', shadow: 'rgba(0,0,0,.35)', disc: 'linear-gradient(135deg,#e05a48,#a3241b)' },
];

export const DEFAULT_INK: InkId = 'blanc';

/** Shadow offset under the text (em). */
export const INK_SHADOW_EM = 0.06;

export function inkById(id: string): Ink {
  return INKS.find(i => i.id === id) ?? INKS[2]!;
}

/** `#rrggbb` → linear-light relative luminance (WCAG). */
export function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0.5;
  const [r, g, b] = [0, 2, 4].map(i => {
    const c = parseInt(m[1]!.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two `#rrggbb` colours (≥ 1). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Below this contrast with the acetate, the panel says « Peu visible sur cette monture » (the choice stays allowed). */
export const LOW_CONTRAST = 2;

export function inkLowContrast(ink: Ink, acetateColor: string | null | undefined): boolean {
  if (!acetateColor) return false;
  return contrastRatio(ink.color, acetateColor) < LOW_CONTRAST;
}
